import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Chip,
    CircularProgress,
    IconButton,
    TextField,
    InputAdornment,
    styled,
    alpha,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import DiamondIcon from '@mui/icons-material/Diamond';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import { dispatch } from '../../../../redux/store';
import { getScriptWiseTrade } from '../../../../redux/slices/operator';
import modifyScriptName from '../../../../utils/modifyScriptName';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '1200px',
        width: '95%',
        maxHeight: '90vh',
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(2),
            width: 'calc(100% - 32px)',
        }
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme, segment }) => ({
    background: segment === 'NSE'
        ? 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)'
        : 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    color: '#fff',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.25),
    }
}));

const StyledTableCell = styled(TableCell)(({ theme, sticky }) => ({
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(1),
    borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
    borderRight: '1px solid rgba(224, 224, 224, 0.3)',
    ...(sticky && {
        position: 'sticky',
        left: 0,
        backgroundColor: '#fff',
        zIndex: 2,
        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
        [theme.breakpoints.down('sm')]: {
            minWidth: '120px',
        }
    }),
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.7rem',
        padding: theme.spacing(0.75),
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    transition: 'background-color 0.2s ease',
    '& .MuiTableCell-root': {
        borderRight: '1px solid rgba(224, 224, 224, 0.3)',
        '&:last-child': {
            borderRight: 'none',
        }
    }
}));

const SummaryBox = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: alpha(theme.palette.primary.main, 0.03),
    borderRadius: '12px',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
    }
}));

const SummaryItem = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(1),
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.75),
    }
}));

const CompactFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 70,
    '& .MuiInputBase-root': {
        color: '#fff',
        fontSize: '0.75rem',
        '&:before, &:after': {
            borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
        },
        '&:hover:not(.Mui-disabled):before': {
            borderBottom: '2px solid rgba(255, 255, 255, 0.7)',
        },
    },
    '& .MuiSelect-icon': {
        color: '#fff',
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: '0.7rem',
    },
    [theme.breakpoints.down('sm')]: {
        minWidth: 55,
        '& .MuiInputBase-root': {
            fontSize: '0.7rem',
        }
    }
}));

const PaginationButton = styled(IconButton)(({ theme }) => ({
    color: '#fff',
    padding: '4px',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:disabled': {
        color: 'rgba(255, 255, 255, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: '3px',
    }
}));

// Format number with integer (no decimals)
const formatNumber = (value) => {
    if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) {
        return '0';
    }
    return Math.round(Number(value)).toLocaleString('en-IN');
};

// Format number with one decimal place
const formatNumberDecimal = (value) => {
    if (value === undefined || value === null || value === '' || Number.isNaN(Number(value))) {
        return '0.0';
    }
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
        return '0.0';
    }
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const getColorForValue = (value) => {
    const numValue = Number(value);
    if (numValue > 0) return '#2E7D32';
    if (numValue < 0) return '#C62828';
    return '#424242';
};

const ActiveTradesDetailsModal = ({ open, onClose, segment, userId, applySharing = false }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [orderBy, setOrderBy] = useState('activeProfitLoss');
    const [order, setOrder] = useState('desc');

    // Use decimal format for MCX segment only when applySharing is true
    const quantityFormatter = (segment === 'MCX' && applySharing) ? formatNumberDecimal : formatNumber;

    const operatorData = useSelector((state) => state?.operator);
    const scriptwiseTrades = operatorData?.scriptwiseTrades || {};
    const scripts = scriptwiseTrades?.docs || [];
    const totalDocs = scriptwiseTrades?.totalDocs || 0;

    // Fetch data when modal opens or parameters change
    useEffect(() => {
        if (open && userId && segment) {
            setLoading(true);
            dispatch(
                getScriptWiseTrade({
                    userId,
                    pageSize: rowsPerPage,
                    pageNumber: page + 1,
                    order,
                    type: 'OPEN',
                    sortBy: orderBy,
                    searchWord: searchTerm,
                    segements: [segment],
                    applySharing,
                })
            ).finally(() => setLoading(false));
        }
    }, [open, userId, segment, page, rowsPerPage, order, orderBy, searchTerm, applySharing]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    // Calculate summary
    const summary = {
        totalScripts: scripts.length,
        totalBuy: scripts.reduce((sum, item) => sum + (Number(item.totalBuyQuantity) || 0), 0),
        totalSell: scripts.reduce((sum, item) => sum + (Number(item.totalSellQuantity) || 0), 0),
        netPosition: scripts.reduce((sum, item) => sum + (Number(item.net) || 0), 0),
        activePL: scripts.reduce((sum, item) => sum + (Number(item.activeProfitLoss) || 0), 0),
        avgBuyRate: scripts.reduce((sum, item) => sum + (Number(item.buy) || 0), 0) / (scripts.length || 1),
        avgSellRate: scripts.reduce((sum, item) => sum + (Number(item.sell) || 0), 0) / (scripts.length || 1),
    };

    const tableHeaders = [
        { id: 'script.tradingsymbol', label: 'Script Name', sortable: true, sticky: true },
        { id: 'net', label: 'Net', sortable: true, align: 'right' },
        { id: 'activeProfitLoss', label: 'Active P/L', sortable: true, align: 'right' },
        { id: 'totalBuyQuantity', label: 'Buy Qty', sortable: true, align: 'right' },
        { id: 'totalSellQuantity', label: 'Sell Qty', sortable: true, align: 'right' },
        { id: 'buy', label: 'Avg Buy', sortable: true, align: 'right' },
        { id: 'sell', label: 'Avg Sell', sortable: true, align: 'right' },
        { id: 'last_price', label: 'CMP', sortable: true, align: 'right' },
    ];

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <StyledDialogTitle segment={segment}>
                {/* Mobile View: Two Rows */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                    {/* Mobile Top Row: Title and Close */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {segment === 'NSE' ? (
                                <CandlestickChartIcon sx={{ fontSize: '1.25rem' }} />
                            ) : (
                                <DiamondIcon sx={{ fontSize: '1.25rem' }} />
                            )}
                            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 700 }}>
                                {segment} ACTIVE POSITIONS
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#fff',
                                padding: '6px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '1.25rem' }} />
                        </IconButton>
                    </Box>

                    {/* Mobile Bottom Row: Controls */}
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-end',
                        justifyContent: 'space-between'
                    }}>
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#fff', fontSize: '1rem' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                flex: 1,
                                maxWidth: '140px',
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '6px',
                                    height: '32px',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.7)',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    padding: '6px 8px',
                                    '&::placeholder': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        opacity: 1,
                                    }
                                }
                            }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                            <CompactFormControl variant="standard" size="small">
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.6rem',
                                        opacity: 0.85,
                                        mb: 0.25,
                                        display: 'block',
                                        lineHeight: 1
                                    }}
                                >
                                    Rows
                                </Typography>
                                <Select
                                    value={rowsPerPage}
                                    onChange={handleChangeRowsPerPage}
                                >
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={75}>75</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </CompactFormControl>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PaginationButton
                                    onClick={(e) => handleChangePage(e, page - 1)}
                                    disabled={page === 0}
                                    size="small"
                                >
                                    <ChevronLeftIcon sx={{ fontSize: '1.1rem' }} />
                                </PaginationButton>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        minWidth: '45px',
                                        textAlign: 'center',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {page + 1} / {Math.ceil(totalDocs / rowsPerPage) || 1}
                                </Typography>

                                <PaginationButton
                                    onClick={(e) => handleChangePage(e, page + 1)}
                                    disabled={page >= Math.ceil(totalDocs / rowsPerPage) - 1}
                                    size="small"
                                >
                                    <ChevronRightIcon sx={{ fontSize: '1.1rem' }} />
                                </PaginationButton>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Desktop View: Single Row */}
                <Box sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                    {/* Left: Icon + Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {segment === 'NSE' ? (
                            <CandlestickChartIcon sx={{ fontSize: '1.5rem' }} />
                        ) : (
                            <DiamondIcon sx={{ fontSize: '1.5rem' }} />
                        )}
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {segment} ACTIVE POSITIONS
                        </Typography>
                    </Box>

                    {/* Center: Search and Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flex: 1, justifyContent: 'flex-end' }}>
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#fff', fontSize: '1rem' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: '200px',
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    fontSize: '0.8rem',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '6px',
                                    height: '32px',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.7)',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    padding: '6px 8px',
                                    '&::placeholder': {
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        opacity: 1,
                                    }
                                }
                            }}
                        />

                        <CompactFormControl variant="standard" size="small">
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    opacity: 0.85,
                                    mb: 0.25,
                                    display: 'block',
                                    lineHeight: 1
                                }}
                            >
                                Rows
                            </Typography>
                            <Select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                            >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={75}>75</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </CompactFormControl>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PaginationButton
                                onClick={(e) => handleChangePage(e, page - 1)}
                                disabled={page === 0}
                                size="small"
                            >
                                <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
                            </PaginationButton>

                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    minWidth: '55px',
                                    textAlign: 'center',
                                    lineHeight: 1.2
                                }}
                            >
                                {page + 1} / {Math.ceil(totalDocs / rowsPerPage) || 1}
                            </Typography>

                            <PaginationButton
                                onClick={(e) => handleChangePage(e, page + 1)}
                                disabled={page >= Math.ceil(totalDocs / rowsPerPage) - 1}
                                size="small"
                            >
                                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
                            </PaginationButton>
                        </Box>
                    </Box>

                    {/* Right: Close Button */}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#fff',
                            padding: '6px',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '1.25rem' }} />
                    </IconButton>
                </Box>
            </StyledDialogTitle>

            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            overflowX: 'auto'
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map((header) => (
                                        <StyledTableCell
                                            key={header.id}
                                            align={header.align || 'left'}
                                            sticky={header.sticky}
                                            sx={{
                                                backgroundColor: segment === 'NSE' ? '#E3F2FD' : '#FFF3E0',
                                                cursor: header.sortable ? 'pointer' : 'default',
                                                fontWeight: 700,
                                                '&:hover': header.sortable ? { backgroundColor: segment === 'NSE' ? '#BBDEFB' : '#FFE0B2' } : {},
                                                ...(header.sticky && {
                                                    zIndex: 3,
                                                })
                                            }}
                                            onClick={() => header.sortable && handleSort(header.id)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: header.align === 'right' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
                                                {header.label}
                                                {header.sortable && orderBy === header.id && (
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                                        {order === 'asc' ? '↑' : '↓'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scripts.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={tableHeaders.length} align="center">
                                            <Typography variant="body2" sx={{ py: 3, color: '#666' }}>
                                                NO ACTIVE POSITIONS FOUND
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    scripts.map((script, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell sticky>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 700, color: '#1a1a1a' }}>
                                                    {modifyScriptName(script.script?.tradingsymbol || 'N/A', script.script?.expiry)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, color: getColorForValue(script.net) }}>
                                                    {quantityFormatter(script.net)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Chip
                                                    label={formatNumber(script.activeProfitLoss)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: script.activeProfitLoss >= 0 ? alpha('#2E7D32', 0.15) : alpha('#C62828', 0.15),
                                                        color: getColorForValue(script.activeProfitLoss),
                                                        fontWeight: 700,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' }
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Chip
                                                    label={quantityFormatter(script.totalBuyQuantity)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha('#2E7D32', 0.1),
                                                        color: '#2E7D32',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' }
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Chip
                                                    label={quantityFormatter(script.totalSellQuantity)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha('#C62828', 0.1),
                                                        color: '#C62828',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' }
                                                    }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }}>
                                                    {formatNumber(script.buy)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600 }}>
                                                    {formatNumber(script.sell)}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 700, color: '#1976D2' }}>
                                                    {formatNumber(script.last_price)}
                                                </Typography>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ p: { xs: 2, sm: 3 }, borderTop: '1px solid rgba(224, 224, 224, 0.4)' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        backgroundColor: segment === 'NSE' ? '#1976D2' : '#FF9800',
                        '&:hover': {
                            backgroundColor: segment === 'NSE' ? '#1565C0' : '#F57C00'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default ActiveTradesDetailsModal;
